import { Grid } from "@mui/material"
import styles from "../../Styles/ComponentStyles/InflationCalculator/InflationCalculator.module.scss"
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartData
} from "chart.js";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

interface HistoricalCpiChartProps {
    data: Array<{ year: number; rate: number }>;
}

const HistoricalCpiChart = ({ data }: HistoricalCpiChartProps) => {
    const options: any = {
        maintainAspectRatio: false,
        layout: {
            padding: {
                left: 10,
                right: 10,
                top: 10,
                bottom: 10
            }
        },
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: false   // ← turn off Chart.js built-in title
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => {
                        const value = context.parsed.y;
                        return `Inflation Rate: ${value?.toFixed(2)}%`;
                    },
                },
                titleFont: { size: 14 },
                bodyFont: { size: 13 },
            }
        },
        tooltip: {
            callbacks: {
                label: (context: any) => {
                    const value = context.parsed.y;
                    return `Inflation Rate: ${value?.toFixed(2)}%`;
                },
            },
            titleFont: { size: 14 },
            bodyFont: { size: 13 },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#475467',       // ← dark gray labels
                    font: { size: 13 },
                    rotation: 0,
                    minRotation: 0,
                    maxRotation: 0,
                    autoSkip: false,
                    callback: function (value: any, index: any) {
                        const year = data?.[index]?.year;
                        return year % 10 === 4 ? year : '';
                    },
                    padding: 10,
                },
                title: {
                    display: true,
                    text: 'Years',
                    color: '#475467',       // ← dark gray
                    font: { size: 14, weight: 500 }
                }
            },
            y: {
                min: -30,                  // ← match image range
                max: 30,
                grid: {
                    drawBorder: false,
                    color: '#98A2B3',   // ← light gray dashed lines
                    borderDash: [4, 4],
                },
                ticks: {
                    color: '#475467',      // ← dark gray
                    font: { size: 13 },
                    stepSize: 10,          // ← 10% steps like image
                    padding: 10,
                    callback: function (value: any) {
                        return value + "%";
                    }
                },
                title: {
                    display: true,
                    text: 'Inflation Rate (%)',
                    color: '#475467',
                    font: { size: 14, weight: 500 }
                },
            },
        },
        elements: {
            bar: {
                borderWidth: 0,
                borderRadius: 2,           // ← slight rounding like image
                borderSkipped: false,
            }
        }
    };

    const chartData: ChartData<'bar'> = {
        labels: data.map(item => item.year.toString()),
        datasets: [
            {
                label: "Inflation Rate",
                backgroundColor: (context: any) => {
                    const value = context.raw;
                    if (value > 10) return '#F3B8AA';
                    if (value > 5) return '#F5CEA4';
                    if (value > 0) return '#009A5C';
                    return 'rgba(238, 73, 51, 1)';                  // Deflation - dark red
                },
                borderColor: (context: any) => {
                    const value = context.raw;
                    if (value > 10) return '#F3B8AA';
                    if (value > 5) return '#F5CEA4';
                    if (value > 0) return '#009A5C';
                    return 'rgba(238, 73, 51, 1)';
                },
                borderWidth: 1,
                data: data.map(item => item.rate),
                barThickness: 'flex' as const,
                maxBarThickness: 8,
                categoryPercentage: 0.8,
                barPercentage: 0.9
            }
        ]
    };

    return (
        <Grid container direction="column" gap="15px" overflow="auto">
            <Grid item xs={12} className={styles.globalBlurWrapperForOutputforChart} padding="18px 18px 16px 18px">

                <Grid item className={styles.chartTitleText} marginBottom={"14px !important"}>
                    Historical Inflation Rate (CPI)
                </Grid>

                <Grid container overflow="auto" style={{ minWidth: '1000px', height: "490px", background: '#F3F6F9', borderRadius: '6px', padding: '8px' }}>
                    <Bar
                        options={options}
                        data={chartData}
                    />
                </Grid>

            </Grid>
        </Grid>
    );
};

export default HistoricalCpiChart;